import React from 'react';
import PropTypes from 'prop-types';

import { Provider } from './PubNubContext';
import getPubNubInstance from './getPubNubInstance';

const onlyUnique = (value, index, self) => self.indexOf(value) === index;

/**
 * Requests which will be either queued or processed
 * @typedef {Object} SubscriptionRequest
 * @property {Array.<String>} channels - The channels the listener cares about
 * @property {Function} onMessage - The callback provided by the consumer to fire on messages
 * @property {Function} onSubscribe - The callback for PubNubSubscribe to handle the subscription
 *
 * Subscriptions which will be stored for reference to clean up later
 * @typedef {Object} Subscription
 * @property {String} id
 * @property {Array.<String>} channels
 * @property {Object} listener
 */

/**
 * A provider that fetches an instance of PubNub and provides `subscribe` and
 * `unsubscribe` methods via React context. This provider maintains a private
 * list of subscriptions to compare against the global list of subscriptions
 * in order to keep necessary channel connections alive and prevent duplicate
 * connections from opening.
 */
class PubNubProvider extends React.Component {
  static propTypes = {
    children: PropTypes.node.isRequired,
  };

  constructor(props) {
    super(props);
    this.subscribe = this.subscribe.bind(this);
    this.unsubscribe = this.unsubscribe.bind(this);
    /**
     * Subscriptions which are managed by this instance of PubNubProvider
     * @property {Array.<Subscription>} subscriptions
     */
    this.subscriptions = [];
    /**
     * Subscription requests which have yet to be processed
     * @property {Array.<SubscriptionRequest>} queue
     */
    this.outstandingSubscriptionRequests = [];
  }

  async componentDidMount() {
    try {
      this.pubNubInstance = await getPubNubInstance();
      this.processOutstandingSubscriptionRequests();
    } catch (err) {
      // Swallow error; it's unrecoverable and non-critical functionality
    }
  }

  componentWillUnmount() {
    if (!this.pubNubInstance) return;
    this.subscriptions.forEach(subscription => this.unsubscribe(subscription));
  }

  /**
   * Gets a flat array of unique channels from an array of subscriptions
   * @param {Array.<Subscription>} subscriptions
   * @returns {Array.<String>}
   */
  getUniqueChannelsInSubscriptions(subscriptions) {
    return subscriptions.flatMap(sub => sub.channels).filter(onlyUnique);
  }

  /**
   * Checks the given channels for new ones and uses the PubNub client to open
   * a new connection for them if there are any.
   * @param {Array.<String>} channels
   */
  addNewChannelsToClient(channels) {
    const uniqueSubscriptionChannels = this.getUniqueChannelsInSubscriptions(
      this.pubNubInstance.subscriptions,
    );
    const newChannels = channels
      .filter(onlyUnique)
      .filter(channel => !uniqueSubscriptionChannels.includes(channel));
    if (newChannels.length) {
      this.pubNubInstance.client.subscribe({ channels: newChannels });
    }
  }

  /**
   * Sets the callback to fire on incoming messages if they match the list of
   * channels that the callback is intended for.
   * @param {Array.<String>} channels - The channels the listener cares about
   * @param {Function} onMessage - The onMessage callback provided by the consumer
   * @return {Object} - The listener, for cleanup purposes
   */
  addListenerToClient(channels, onMessage) {
    const listener = {
      message: m => {
        if (channels.includes(m.channel)) {
          onMessage(m.message);
        }
      },
    };
    this.pubNubInstance.client.addListener(listener);
    return listener;
  }

  /**
   * Processes any outstanding subscription requests. When we instantiate the
   * PubNubProvider, we must request a PubNub client asynchronously. This bundles
   * all of our calls to be executed once the client has resolved.
   */
  processOutstandingSubscriptionRequests() {
    if (!this.outstandingSubscriptionRequests.length) return;
    this.outstandingSubscriptionRequests.forEach(subscriptionRequest =>
      this.handleSubscribe(subscriptionRequest),
    );
    this.outstandingSubscriptionRequests = [];
  }

  /**
   * The private subscribe method that interacts directly with the PubNub client
   * to subscribe, add listeners, and store them in state
   * @param {SubscriptionRequest} subscriptionRequest
   */
  handleSubscribe({ channels, onMessage, onSubscribe }) {
    this.addNewChannelsToClient(channels);
    const listener = this.addListenerToClient(channels, onMessage);
    const subscription = {
      id: this.pubNubInstance.getSubscriptionId(),
      channels,
      listener,
    };
    // Store local subscriptions
    this.subscriptions = [...this.subscriptions, subscription];
    // Store global subscriptions
    this.pubNubInstance.subscriptions = [...this.pubNubInstance.subscriptions, subscription];
    onSubscribe(subscription);
  }

  /**
   * Public method for requesting a new subscription be added to the client
   * @param {SubscriptionRequest} subscriptionRequest
   */
  subscribe(subscriptionRequest) {
    if (!this.pubNubInstance) {
      this.outstandingSubscriptionRequests = [
        ...this.outstandingSubscriptionRequests,
        subscriptionRequest,
      ];
      return;
    }
    this.handleSubscribe(subscriptionRequest);
  }

  /**
   * Public method for removing a subscription, handling removing the listener
   * and closing any channels if required.
   * @param {Subscription} subscription
   */
  unsubscribe(subscription) {
    if (!this.pubNubInstance) return;
    const { subscriptions } = this.pubNubInstance;
    const allSubscriptionsButCurrent = sub => sub.id !== subscription.id;
    const remainingSubscriptions = subscriptions.filter(allSubscriptionsButCurrent);
    const remainingLocalSubscriptions = this.subscriptions.filter(allSubscriptionsButCurrent);
    const currentChannels = this.getUniqueChannelsInSubscriptions(subscriptions);
    const remainingChannels = this.getUniqueChannelsInSubscriptions(remainingSubscriptions);
    const removableChannels = currentChannels.filter(
      channel => !remainingChannels.includes(channel),
    );

    this.pubNubInstance.client.removeListener(subscription.listener);
    if (removableChannels.length) {
      this.pubNubInstance.client.unsubscribe({ channels: removableChannels });
    }
    // Store local subscriptions
    this.subscriptions = remainingLocalSubscriptions;
    // Store global subscriptions
    this.pubNubInstance.subscriptions = remainingSubscriptions;
  }

  render() {
    return (
      <Provider value={{ subscribe: this.subscribe, unsubscribe: this.unsubscribe }}>
        {this.props.children}
      </Provider>
    );
  }
}

export default PubNubProvider;
